import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
/**
 * Denise Augusta brand theme — Chakra UI v3 system.
 *
 * Palette and type carried over from the original static site:
 * Cormorant Garamond display serif + DM Sans body, deep navy-blue heads,
 * periwinkle mids, a lime accent line, and terracotta hover states on a
 * warm linen paper ground.
 */
const config = defineConfig({
    globalCss: {
        'html, body': {
            margin: 0,
            padding: 0,
            fontFamily: 'body',
            color: 'ink.body',
            overflowX: 'hidden',
        },
        // Page color lives on html; body stays transparent so fixed background
        // layers with negative z-index (the home page's desk-wallpaper) paint
        // above the canvas instead of being covered by body's background.
        html: { scrollBehavior: 'smooth', bg: 'paper.warm' },
        body: {
            bg: 'transparent',
            // Chakra's reset antialiases text, which renders thinner and lighter
            // on macOS than the original static site — restore browser defaults
            // so the light-weight serif faces keep their weight.
            // (spread — Chakra's SystemStyleObject type doesn't list vendor props)
            ...{ '-webkit-font-smoothing': 'auto', '-moz-osx-font-smoothing': 'auto' },
        },
        '::-webkit-scrollbar': { width: '4px' },
        '::-webkit-scrollbar-track': { background: '#E8E5E0' },
        '::-webkit-scrollbar-thumb': { background: '#3D52A8', borderRadius: '20px' },
        // Portal rich text fields (Tiptap/ProseMirror content isn't reachable via Chakra props).
        '.rich-text-field-content .tiptap': {
            minHeight: '90px',
            padding: '0.6rem 0.9rem',
            fontFamily: 'body',
            fontSize: '0.85rem',
            color: 'ink.body',
            outline: 'none',
        },
        '.rich-text-field-content .tiptap p': { margin: 0 },
        '.rich-text-field-content .tiptap p + p': { marginTop: '0.7em' },
        // The preflight reset above sets `ol, ul { list-style: none }` site-wide
        // (other lists on the site draw their own bullet glyphs) — restore real
        // markers here so Tiptap's lists don't render as indented text only.
        '.rich-text-field-content .tiptap ul, .rich-text-field-content .tiptap ol': {
            margin: '0.5em 0',
            paddingInlineStart: '1.3em',
        },
        '.rich-text-field-content .tiptap ul': { listStyleType: 'disc' },
        '.rich-text-field-content .tiptap ol': { listStyleType: 'decimal' },
        '.rich-text-field-content .tiptap li': { marginBottom: '0.25em' },
        '.rich-text-field-content .tiptap li::marker': { color: 'inherit' },
    },
    theme: {
        keyframes: {
            greenFlash: {
                '0%': { backgroundColor: 'rgba(181,224,97,0)' },
                '12%': { backgroundColor: 'rgba(181,224,97,0.62)' },
                '100%': { backgroundColor: 'rgba(181,224,97,0)' },
            },
            btnGreenFlash: {
                '0%': { opacity: '0' },
                '12%': { opacity: '0.62' },
                '100%': { opacity: '0' },
            },
            ticker: {
                from: { transform: 'translateX(0)' },
                to: { transform: 'translateX(-50%)' },
            },
            fadeUp: {
                from: { opacity: '0', transform: 'translateY(22px)' },
                to: { opacity: '1', transform: 'translateY(0)' },
            },
        },
        tokens: {
            colors: {
                brand: {
                    // Deep navy — headings ("green-head" in the legacy CSS)
                    ink: { value: '#1F2D5C' },
                    // Periwinkle blue — mid accents, links, section labels
                    mid: { value: '#3D52A8' },
                    light: { value: '#5C72BF' },
                    pale: { value: '#C5CDE4' },
                    wash: { value: '#EAEDF5' },
                    ultra: { value: 'rgba(61,82,168,0.08)' },
                    // Lime accent rules + hover flash
                    lime: { value: '#B5E061' },
                    // Terracotta hover text
                    terracotta: { value: '#BC5A4D' },
                },
                paper: {
                    white: { value: '#FFFFFF' },
                    warm: { value: '#F5F4F1' },
                    off: { value: '#EDECEA' },
                    linen: { value: '#E8E5E0' },
                    greyPale: { value: '#DDDAD5' },
                    border: { value: '#C8C4BE' },
                },
                ink: {
                    body: { value: '#3F3D3B' },
                    soft: { value: '#7A7774' },
                    muted: { value: '#5A5854' },
                    light: { value: '#6F6C68' },
                },
            },
            fonts: {
                heading: { value: "'Cormorant Garamond', Georgia, serif" },
                body: { value: "'DM Sans', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif" },
            },
        },
        semanticTokens: {
            colors: {
                fg: { value: '{colors.ink.body}' },
                'fg.muted': { value: '{colors.ink.muted}' },
                accent: { value: '{colors.brand.mid}' },
            },
        },
    },
});
export const system = createSystem(defaultConfig, config);
//# sourceMappingURL=theme.js.map